import React, { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { FrozenDeliveryType, StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { PackageSize } from 'types/storeDeliveryFee'
import { useSelect, useSelectHoursToDeadline, useSelectNumber, useString } from 'hooks/form'
import { useStoreDeliveryBreadChoices } from 'hooks/storeDeliveryBread'
import { UseStoreDeliveryBreadChoices } from 'hooks/storeDeliveryBread/useStoreDeliveryBreadChoices'
import { FirebaseDocRefType } from 'firebase/index'
import { openCautionModal } from 'reducks/modal/operations'
import { useDispatch } from 'react-redux'
import { initStoreDeliveryBread } from 'reducks/storeDeliveryBread/operations'
import { StoreDefaultDeliveryBreadType } from 'types/storeDefault'

export type UseStoreDeliveryBread = {
  deliveryCompany: string
  frozenDeliveryType: FrozenDeliveryType
  maximumOrder: number
  packageSize: PackageSize | ''
  handleFrozenDeliveryType: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleIsFrozenDelivery: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleHowToEat: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleHowToStore: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  hoursToDeadline: number
  howToEat: string
  howToStore: string
  isFrozenDelivery: boolean
  selectDateToDeadline: (value: string) => void
  selectTimeToDeadline: (value: string) => void
  selectDeliveryCompany: (value: string) => void
  selectMaximumOrder: (value: string) => void
  selectPackageSize: (value: PackageSize | '') => void
  toggleCheckbox: () => void
} & UseStoreDeliveryBreadChoices

const useStoreDeliveryBread = (
  breadRef: FirebaseDocRefType,
  isTypeDelivery: boolean,
  storeRef: FirebaseDocRefType,
  storeDeliveryBread: StoreDeliveryBreadType,
  storeDefaultDeliveryBread: StoreDefaultDeliveryBreadType | undefined,
  setIsTypeDelivery: Dispatch<SetStateAction<boolean>>
) => {
  const dispatch = useDispatch()
  const [maximumOrder, setMaximumOrder] = useState<number>(0)
  const {
    state: packageSize,
    handleState: selectPackageSize,
    setState: setPackageSize,
  } = useSelect<PackageSize | ''>('')
  const {
    state: deliveryCompany,
    handleState: selectDeliveryCompany,
    setState: setDeliveryCompany,
  } = useSelect<string>('')
  const [isFrozenDelivery, setIsFrozenDelivery] = useState<boolean>(false)
  const [frozenDeliveryType, setFrozenDeliveryType] = useState<FrozenDeliveryType>('')
  const { hoursToDeadline, setHoursToDeadline, selectDateToDeadline, selectTimeToDeadline } = useSelectHoursToDeadline()
  const { state: howToEat, handleState: handleHowToEat, setState: setHowToEat } = useString<string>('')
  const { state: howToStore, handleState: handleHowToStore, setState: setHowToStore } = useString<string>('')
  const { addChoice, clearChoice, choices, deleteChoice, handleChoice, initChoice, setChoices } =
    useStoreDeliveryBreadChoices()

  const handleIsFrozenDelivery = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsFrozenDelivery((prevState) => {
        // trueからfalseに変更する時
        if (prevState) {
          setFrozenDeliveryType('')
        }
        return !prevState
      })
    },
    [setIsFrozenDelivery]
  )

  const handleFrozenDeliveryType = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const id = event.target.id
      setFrozenDeliveryType((prevState) => {
        return prevState !== id ? (id as FrozenDeliveryType) : ''
      })
    },
    [setFrozenDeliveryType]
  )

  const selectMaximumOrder = useSelectNumber(setMaximumOrder)

  const toggleCheckbox = useCallback(() => {
    if (isTypeDelivery) {
      dispatch(
        openCautionModal(
          '変更する',
          'キャンセル',
          'すでに登録されている情報は削除されてしまいます。一時的な変更であれば、在庫を０にするや、お届け可能日を設定しないなどお試しください。',
          () => {
            setIsTypeDelivery(false)
            setIsFrozenDelivery(false)
            setFrozenDeliveryType('')
            setMaximumOrder(0)
            setPackageSize('')
            setDeliveryCompany('')
            setHoursToDeadline(24)
            setHowToEat('')
            setHowToStore('')
            clearChoice()
            dispatch(initStoreDeliveryBread())
          },
          '注文タイプを変更します'
        )
      )
    } else {
      setIsTypeDelivery(true)
    }
  }, [
    setIsFrozenDelivery,
    setFrozenDeliveryType,
    setIsTypeDelivery,
    setMaximumOrder,
    setPackageSize,
    setDeliveryCompany,
    setHoursToDeadline,
    setHowToEat,
    setHowToStore,
    clearChoice,
    isTypeDelivery,
  ])

  useEffect(() => {
    const isDeliveryBreadCase = Object.values(storeDeliveryBread).some((value) =>
      Array.isArray(value) ? value.length > 0 : !!value
    )
    const isDefaultDeliveryBreadCase = !isDeliveryBreadCase && storeDefaultDeliveryBread
    if (isDeliveryBreadCase) {
      setFrozenDeliveryType(storeDeliveryBread.frozen_delivery_type || '')
      setMaximumOrder(storeDeliveryBread.maximum_order)
      setPackageSize(storeDeliveryBread.package_size)
      setDeliveryCompany(storeDeliveryBread.delivery_company)
      setIsFrozenDelivery(storeDeliveryBread.is_frozen_delivery)
      setHoursToDeadline(storeDeliveryBread.hours_to_deadline)
      setHowToEat(storeDeliveryBread.how_to_eat)
      setHowToStore(storeDeliveryBread.how_to_store)
      setChoices(storeDeliveryBread.choices)
    } else if (isDefaultDeliveryBreadCase) {
      setFrozenDeliveryType(storeDefaultDeliveryBread.frozen_delivery_type || '')
      setMaximumOrder(storeDefaultDeliveryBread.maximum_order)
      setPackageSize(storeDefaultDeliveryBread.package_size)
      setDeliveryCompany(storeDefaultDeliveryBread.delivery_company)
      setIsFrozenDelivery(storeDefaultDeliveryBread.is_frozen_delivery)
      setHoursToDeadline(storeDefaultDeliveryBread.hours_to_deadline)
      setHowToEat(storeDefaultDeliveryBread.how_to_eat)
      setHowToStore(storeDefaultDeliveryBread.how_to_store)
      setChoices(storeDefaultDeliveryBread.choices)
    } else {
      return
    }
  }, [storeDeliveryBread, storeDefaultDeliveryBread])

  return {
    deliveryCompany,
    frozenDeliveryType,
    maximumOrder,
    packageSize,
    handleFrozenDeliveryType,
    handleIsFrozenDelivery,
    handleHowToEat,
    handleHowToStore,
    hoursToDeadline,
    howToEat,
    howToStore,
    isFrozenDelivery,
    selectDateToDeadline,
    selectTimeToDeadline,
    selectDeliveryCompany,
    selectMaximumOrder,
    selectPackageSize,
    toggleCheckbox,
    addChoice,
    clearChoice,
    choices,
    deleteChoice,
    handleChoice,
    initChoice,
    setChoices,
  }
}

export default useStoreDeliveryBread
